import {Button, Form, Input, InputNumber, Modal, Select, Upload} from "antd";
import {useFormik} from "formik";
import React, {useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import {useStore} from "../../../hooks/useStore";
import {UploadOutlined} from "@ant-design/icons";
import {creditLogsObjectionTypes} from "../../../constants/constants";
import {
    StyledModal,
    StyledForm,
    StyledSelect,
    StyledInputNumber,
    StyledInput,
    StyledUpload,
    StyledUploadButton
} from "./ObjectionModal.styles";

const objectionReasons = Object.freeze({
    incorrectPayment: {
        name: "incorrectPayment",
        text: "Ödeme İtirazı"
    },
    incorrectDistance: {
        name: "incorrectDistance",
        text: "Mesafe İtirazı"
    },
    incorrectDelivery: {
        name: "incorrectDelivery",
        text: "Teslimat İtirazı"
    },
    other: {
        name: "other",
        text: "Diğer"
    }
});

export const ObjectionModal = ({visibility, onCancel, subCompanyId, deliveryId, orderId}) => {
    const [confirmLoading, setConfirmLoading] = useState(false);
    const stores = useStore();
    const location = useLocation();
    const {Dragger} = Upload;
    const [fileList, setFileList] = useState([]);
    const isCustomerPanel = location.pathname.startsWith("/musteri");

    const initialValues = {
        type: undefined,
        subcompanyId: subCompanyId,
        deliveryId,
        orderId,
        reason: "",
        requestedAmount: null,
        note: ""
    };

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        onSubmit: async values => {
            const errors = {};
            if (!values.type) {
                errors.type = "İtiraz Tipi Seçiniz";
            }
            if (!values.requestedAmount || values.requestedAmount < 0) {
                errors.requestedAmount = !values.requestedAmount
                    ? "Tutar giriniz"
                    : "Tutar negatif olamaz";
            }
            if (!values.reason) {
                errors.reason = "İtiraz Nedeni Seçiniz";
            }
            if (fileList.length === 0) {
                errors.fileList = "Belge Yükleyiniz";
            }

            if (Object.keys(errors).length > 0) {
                formik.setErrors(errors);
                return;
            }
            setConfirmLoading(true);
            try {
                const fileIdList = await handleFileChange(fileList);
                const payload = {
                    ...values,
                    fileIds: [...fileIdList]
                };
                const result = await stores.companyStore.createObjectionsCreditLog(payload);
                if (result) {
                    onCancel(true);
                }
            } catch (error) {
                Sentry.captureException(error);
            } finally {
                formik.resetForm();
                setFileList([]);
                setConfirmLoading(false);
            }
        }
    });

    const dummyRequest = ({file, onSuccess}) => {
        setFileList(prev => [...prev, file]);
        formik.setFieldError("fileList", undefined);
        setTimeout(() => {
            onSuccess("ok");
        }, 0);
    };

    const handleFileChange = async files => {
        const uploadPromises = files.map(file => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();

                reader.onloadend = async () => {
                    const base64String = reader.result.split(",")[1];
                    const payload = {
                        file: {
                            originalname: file.name,
                            mimetype: file.type,
                            buffer: base64String
                        },
                        directory: "objection"
                    };

                    try {
                        const response = await stores.fileStore.upload(payload);
                        if (response.result && response.result.id) {
                            resolve(response.result.id);
                        } else {
                            reject(new Error("Yükleme başarısız oldu."));
                        }
                    } catch (error) {
                        console.error("Dosya yükleme hatası:", error);
                        reject(error);
                    }
                };

                reader.readAsDataURL(file);
            });
        });

        try {
            const newFileIds = await Promise.all(uploadPromises);
            return newFileIds;
        } catch (error) {
            console.error("Dosya yükleme sırasında bir hata oluştu:", error);
        }
    };

    const {values, handleSubmit, handleChange, setFieldValue, setFieldError} = formik;

    return (
        <React.Fragment>
            {isCustomerPanel ? (
                <StyledModal
                    visible={visibility}
                    centered
                    title={"İtiraz Nedeni"}
                    okText={"Tamam"}
                    onOk={handleSubmit}
                    confirmLoading={confirmLoading}
                    cancelText={"İptal"}
                    onCancel={() => {
                        onCancel(false);
                        formik.resetForm();
                    }}
                    width={600}
                >
                    <StyledForm
                        labelCol={{span: 24}}
                        wrapperCol={{span: 24}}
                        onFinish={handleSubmit}
                    >
                        <Form.Item
                            label="İtiraz Tipi"
                            validateStatus={formik.errors.type ? "error" : ""}
                            help={formik.errors.type}
                        >
                            <StyledSelect
                                name="type"
                                value={values.type}
                                onChange={value => {
                                    setFieldValue("type", value);
                                    setFieldError("type", undefined);
                                }}
                            >
                                {creditLogsObjectionTypes?.map((type, index) => (
                                    <Select.Option key={type.value} value={type?.value}>
                                        {type?.label}
                                    </Select.Option>
                                ))}
                            </StyledSelect>
                        </Form.Item>
                        <Form.Item
                            label="Talep Edilen Tutar"
                            validateStatus={formik.errors.requestedAmount ? "error" : ""}
                            help={formik.errors.requestedAmount}
                        >
                            <StyledInputNumber
                                style={{width: "100%"}}
                                name="requestedAmount"
                                type="number"
                                value={values.requestedAmount}
                                onChange={value => {
                                    setFieldValue("requestedAmount", value);
                                    setFieldError("requestedAmount", undefined);
                                }}
                                min="0"
                            />
                        </Form.Item>
                        <Form.Item
                            label="İtiraz Nedeni"
                            validateStatus={formik.errors.reason ? "error" : ""}
                            help={formik.errors.reason}
                        >
                            <StyledSelect
                                name="reason"
                                value={values.reason}
                                onChange={value => {
                                    setFieldValue("reason", value);
                                    setFieldError("reason", undefined);
                                }}
                            >
                                <Select.Option value={objectionReasons?.incorrectPayment?.name}>
                                    {objectionReasons?.incorrectPayment?.text}
                                </Select.Option>
                                <Select.Option value={objectionReasons?.incorrectDistance?.name}>
                                    {objectionReasons?.incorrectDistance?.text}
                                </Select.Option>
                                <Select.Option value={objectionReasons?.incorrectDelivery?.name}>
                                    {objectionReasons?.incorrectDelivery?.text}
                                </Select.Option>
                                <Select.Option value={objectionReasons?.other?.name}>
                                    {objectionReasons?.other?.text}
                                </Select.Option>
                            </StyledSelect>
                        </Form.Item>
                        <Form.Item label="Not">
                            <StyledInput name="note" value={values.note} onChange={handleChange} />
                        </Form.Item>
                        <Form.Item
                            label={"Belge Yükle"}
                            data-testid="fileDraggerContainer"
                            validateStatus={formik.errors.fileList ? "error" : ""}
                            help={formik.errors.fileList}
                        >
                            <StyledUpload
                                fileList={fileList}
                                customRequest={dummyRequest}
                                listType="picture"
                                multiple
                                accept=".png,.jpg,.jpeg,.heic,.hevc,.tiff,.gif,.pdf"
                                onRemove={file => {
                                    const newFileList = fileList.filter(f => f.uid !== file.uid);
                                    setFileList(newFileList);
                                    setFieldError("fileList", undefined);
                                }}
                            >
                                <StyledUploadButton
                                    data-testid="uploadButton"
                                    icon={<UploadOutlined />}
                                >
                                    Yükle
                                </StyledUploadButton>
                            </StyledUpload>
                        </Form.Item>
                    </StyledForm>
                </StyledModal>
            ) : (
                <Modal
                    visible={visibility}
                    centered
                    title={"İtiraz Nedeni"}
                    okText={"Tamam"}
                    onOk={handleSubmit}
                    confirmLoading={confirmLoading}
                    cancelText={"İptal"}
                    onCancel={() => {
                        onCancel(false);
                        formik.resetForm();
                    }}
                    width={600}
                >
                    <Form labelCol={{span: 24}} wrapperCol={{span: 24}} onFinish={handleSubmit}>
                        <Form.Item
                            label="İtiraz Tipi"
                            validateStatus={formik.errors.type ? "error" : ""}
                            help={formik.errors.type}
                        >
                            <Select
                                name="type"
                                value={values.type}
                                onChange={value => {
                                    setFieldValue("type", value);
                                    setFieldError("type", undefined);
                                }}
                            >
                                {creditLogsObjectionTypes?.map((type, index) => (
                                    <Select.Option key={type.value} value={type?.value}>
                                        {type?.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            label="Talep Edilen Tutar"
                            validateStatus={formik.errors.requestedAmount ? "error" : ""}
                            help={formik.errors.requestedAmount}
                        >
                            <InputNumber
                                style={{width: "100%"}}
                                name="requestedAmount"
                                type="number"
                                value={values.requestedAmount}
                                onChange={value => {
                                    setFieldValue("requestedAmount", value);
                                    setFieldError("requestedAmount", undefined);
                                }}
                                min="0"
                            />
                        </Form.Item>
                        <Form.Item
                            label="İtiraz Nedeni"
                            validateStatus={formik.errors.reason ? "error" : ""}
                            help={formik.errors.reason}
                        >
                            <Select
                                name="reason"
                                value={values.reason}
                                onChange={value => {
                                    setFieldValue("reason", value);
                                    setFieldError("reason", undefined);
                                }}
                            >
                                <Select.Option value={objectionReasons?.incorrectPayment?.name}>
                                    {objectionReasons?.incorrectPayment?.text}
                                </Select.Option>
                                <Select.Option value={objectionReasons?.incorrectDistance?.name}>
                                    {objectionReasons?.incorrectDistance?.text}
                                </Select.Option>
                                <Select.Option value={objectionReasons?.incorrectDelivery?.name}>
                                    {objectionReasons?.incorrectDelivery?.text}
                                </Select.Option>
                                <Select.Option value={objectionReasons?.other?.name}>
                                    {objectionReasons?.other?.text}
                                </Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item label="Not">
                            <Input name="note" value={values.note} onChange={handleChange} />
                        </Form.Item>
                        <Form.Item
                            label={"Belge Yükle"}
                            data-testid="fileDraggerContainer"
                            validateStatus={formik.errors.fileList ? "error" : ""}
                            help={formik.errors.fileList}
                        >
                            <Upload
                                fileList={fileList}
                                customRequest={dummyRequest}
                                listType="picture"
                                multiple
                                accept=".png,.jpg,.jpeg,.heic,.hevc,.tiff,.gif,.pdf"
                                onRemove={file => {
                                    const newFileList = fileList.filter(f => f.uid !== file.uid);
                                    setFileList(newFileList);
                                    setFieldError("fileList", undefined);
                                }}
                            >
                                <Button data-testid="uploadButton" icon={<UploadOutlined />}>
                                    Yükle
                                </Button>
                            </Upload>
                        </Form.Item>
                    </Form>
                </Modal>
            )}
        </React.Fragment>
    );
};
